import { useState, useEffect, useCallback } from "react";
import { Bell, ClipboardList, Building2, Users, Briefcase, ShieldCheck, Check } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { AdminHeader } from "@/components/admin/AdminDashboard";

const CARDS = [
  { key: "applications", label: "Applications", icon: ClipboardList, tab: "applications", tone: "text-[#0099FF]" },
  { key: "employer_requests", label: "Talent Requests", icon: Building2, tab: "employers", tone: "text-[#FF5E1E]" },
  { key: "users", label: "Registered Users", icon: Users, tab: "users", tone: "text-[#F4F6F8]" },
  { key: "workforce_ready", label: "Workforce Ready", icon: ShieldCheck, tab: "users", tone: "text-[#10B981]" },
  { key: "jobs", label: "Live Job Listings", icon: Briefcase, tab: "jobs", tone: "text-[#FFB800]" },
  { key: "candidates", label: "Candidates", icon: Users, tab: "users", tone: "text-[#0099FF]" },
];

export default function AdminOverview({ stats, onGo, onRefresh }) {
  const [notifs, setNotifs] = useState([]);

  const load = useCallback(async () => {
    try { setNotifs(await adminApi.notifications()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async () => {
    await adminApi.readNotifications();
    await load();
    onRefresh?.();
  };

  const unread = notifs.filter((n) => !n.read);

  return (
    <div>
      <AdminHeader eyebrow="CONTROL DECK — OVERVIEW" title="Command Center" sub="Live view of your maritime workforce pipeline." />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {CARDS.map((c) => (
          <button
            key={c.key}
            data-testid={`overview-card-${c.key}`}
            onClick={() => onGo?.(c.tab)}
            className="text-left bg-[#10161F] border border-white/10 hover:border-[#FF5E1E]/50 p-5 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <c.icon size={18} className="text-[#8FA3B8] group-hover:text-[#FF5E1E] transition-colors" />
            </div>
            <div className={`font-display font-black text-4xl mt-3 ${c.tone}`}>{stats ? stats[c.key] ?? 0 : "—"}</div>
            <div className="font-mono-tech text-[9px] tracking-[0.14em] text-[#8FA3B8] mt-1 uppercase">{c.label}</div>
          </button>
        ))}
      </div>

      {/* Notifications */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bell size={16} className="text-[#FF5E1E]" />
            <span className="font-mono-tech text-[11px] tracking-[0.2em] text-white uppercase">Notifications</span>
            {unread.length > 0 && <span className="min-w-[20px] h-5 px-1.5 grid place-items-center bg-[#FF5E1E] text-[#0B0F14] text-[10px] font-bold">{unread.length}</span>}
          </div>
          {unread.length > 0 && (
            <button data-testid="notifications-mark-read" onClick={markRead} className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[#8FA3B8] hover:text-white hover:border-[#FF5E1E]/60 transition-colors font-mono-tech text-[10px] tracking-[0.12em] uppercase">
              <Check size={13} /> Mark all read
            </button>
          )}
        </div>

        <div className="space-y-2" data-testid="notifications-list">
          {notifs.length === 0 && <div className="text-sm text-[#8FA3B8] border border-dashed border-white/10 p-6 text-center">No notifications yet. New applications and talent requests will appear here.</div>}
          {notifs.slice(0, 30).map((n) => (
            <div key={n.id} className={`flex items-start gap-3 border p-4 ${n.read ? "border-white/8 bg-[#10161F]/50" : "border-[#FF5E1E]/30 bg-[#FF5E1E]/5"}`}>
              <span className={`mt-1 h-2 w-2 rounded-full ${n.read ? "bg-white/20" : "bg-[#FF5E1E]"}`} />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold uppercase text-sm">{n.title}</div>
                <div className="text-sm text-[#B8C4D0] truncate">{n.body}</div>
                <div className="font-mono-tech text-[9px] tracking-[0.12em] text-[#8FA3B8] mt-1">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
